import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LuPlus, LuFilter, LuX } from 'react-icons/lu';
import { FiAlertTriangle } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { useBuilding } from '../contexts/BuildingContext';
import { canViewAllFaultReports } from '../utils/roleCheck';
import { toast } from '../hooks/use-toast';
import CreateFaultForm from '../components/FaultReports/CreateFaultForm';
import FaultReportCard from '../components/FaultReports/FaultReportCard';
import FaultFilters from '../components/FaultReports/FaultFilters';
import { createFaultReportNotifications } from '../utils/notificationHelpers';

export default function FaultReports() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { currentBuilding, currentProperty, loading: contextLoading } = useBuilding();
  const navigate = useNavigate();
  const [faults, setFaults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    scope: 'all',
    status: 'all',
    category: 'all',
    priority: 'all',
    sortBy: 'newest'
  });

  useEffect(() => {
    if (!contextLoading && user) {
      loadFaults();
    }
  }, [currentBuilding, currentProperty, filters, contextLoading, user]);

  const loadFaults = async () => {
    try {
      setLoading(true);
      const queryFilters = [];
      if (currentProperty?.id) {
        queryFilters.push({ name: 'property_id', op: 'Equal', value: Number(currentProperty.id) });
      }

      // Handle Building Filter
      if (!canViewAllFaultReports(user.role)) {
        // Residents only see their building or property wide
        if (currentBuilding?.id && !currentBuilding.isAll) {
          if (currentBuilding.isPropertyWide) {
            queryFilters.push({ name: 'scope', op: 'Equal', value: 'PROPERTY' });
          } else {
            queryFilters.push({ name: 'building_id', op: 'Equal', value: Number(currentBuilding.id) });
          }
        }
      } else {
        // Managers/Admins can filter by building choice
        if (currentBuilding?.id && !currentBuilding.isAll) {
          if (currentBuilding.isPropertyWide) {
            queryFilters.push({ name: 'scope', op: 'Equal', value: 'PROPERTY' });
          } else {
            queryFilters.push({ name: 'building_id', op: 'Equal', value: Number(currentBuilding.id) });
          }
        }
      }

      if (filters.status !== 'all') {
        queryFilters.push({ name: 'status', op: 'Equal', value: filters.status });
      }

      const { data, error } = await window.ezsite.apis.tablePage(70649, {
        PageNo: 1,
        PageSize: 100,
        OrderByField: 'created_at',
        IsAsc: false,
        Filters: queryFilters
      });

      if (error) throw error;
      const rawFaults = data?.List || [];

      let userMap = {};
      try {
        const { data: usersData } = await window.ezsite.apis.tablePage(70648, {
          PageNo: 1,
          PageSize: 1000
        });

        userMap = (usersData?.List || []).reduce((acc, u) => {
          acc[String(u.id)] = u.name || u.Name || 'Unknown';
          return acc;
        }, {});
      } catch (e) {
        console.error('Failed to fetch user profiles:', e);
      }

      const enrichedFaults = await Promise.all(
        rawFaults.map(async (fault) => {
          const reporterId = fault.created_by || fault.reporter_id;
          let reporterName = 'Unknown User';
          if (String(reporterId) === String(user.id)) {
            reporterName = 'You';
          } else if (reporterId && userMap[String(reporterId)]) {
            reporterName = userMap[String(reporterId)];
          } else if (reporterId) {
            reporterName = `User #${reporterId}`;
          }

          try {
            const { data: photoData } = await window.ezsite.apis.tablePage(70650, {
              PageNo: 1,
              PageSize: 10, // Increased to fetch all attachments so we can find the image
              Filters: [{ name: 'fault_report_id', op: 'Equal', value: Number(fault.id) }]
            });
            return {
              ...fault,
              photos: photoData?.List || [],
              reporter_name: reporterName
            };
          } catch (e) {
            return {
              ...fault,
              photos: [],
              reporter_name: reporterName
            };
          }
        })
      );

      setFaults(enrichedFaults);
    } catch (error) {
      console.error('Failed to load faults:', error);
      toast({ title: t('common.error'), description: t('faultReports.loadFailed'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFault = async (faultData, uploadedPhotos) => {
    try {
      const now = new Date().toISOString();
      const payload = {
        ...faultData,
        property_id: currentProperty?.id ? Number(currentProperty.id) : null,
        building_id: faultData.scope === 'BUILDING' ? Number(faultData.building_id) : null,
        created_by: Number(user.id),
        reporter_id: Number(user.id),
        status: 'New',
        created_at: now,
        updated_at: now,
        status_history: JSON.stringify([{ status: 'New', timestamp: now, changed_by: user.id }])
      };

      const { data, error } = await window.ezsite.apis.tableCreate(70649, payload);
      if (error) throw error;

      const newReportId = data?.id || data?.ID || (typeof data === 'number' || typeof data === 'string' ? data : null);
      if (!newReportId) throw new Error('Failed to retrieve new report ID');

      if (uploadedPhotos?.length > 0) {
        await Promise.all(
          uploadedPhotos.map((photo) => window.ezsite.apis.tableCreate(70650, {
            fault_report_id: Number(newReportId),
            file_url: photo.url,
            file_name: photo.name,
            uploaded_by: Number(user.id),
            created_at: now
          }))
        );
      }

      toast({ title: t('common.success'), description: t('faultReports.reportSubmitted') });
      setShowCreateModal(false);
      loadFaults();

      createFaultReportNotifications({ ...payload, id: newReportId }, user.name).catch((e) =>
      console.error('Failed to send fault report notifications:', e)
      );
    } catch (error) {
      console.error('Submit failed:', error);
      toast({ title: t('common.error'), description: t('faultReports.submitFailed'), variant: 'destructive' });
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-600 rounded-2xl shadow-lg shadow-blue-200">
            <FiAlertTriangle className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{t('faultReports.title')}</h1>
            <p className="text-sm text-gray-500 font-bold uppercase tracking-wider">
              {loading ? t('faultReports.syncing') : t('faultReports.issuesTracked', { count: faults.length })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-5 py-3 rounded-2xl border transition-all font-bold text-sm ${showFilters ? 'bg-gray-900 text-white border-gray-900 shadow-lg' : 'bg-white text-gray-700 border-gray-100 hover:bg-gray-50'}`}>

            <LuFilter className="w-4 h-4" />
            <span className="hidden sm:inline">{t('faultReports.filters')}</span>
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-2xl hover:bg-blue-700 shadow-lg shadow-blue-100 transition-all font-bold text-sm active:scale-95">

            <LuPlus className="w-5 h-5" />
            <span className="hidden sm:inline">{t('faultReports.reportIssue')}</span>
          </button>
        </div>
      </div>

      {showFilters &&
      <div className="animate-in slide-in-from-top-4 duration-300 mb-8">
          <FaultFilters filters={filters} onFiltersChange={setFilters} />
        </div>
      }

      {loading && faults.length === 0 ?
      <div className="flex flex-col items-center justify-center py-32 bg-white rounded-[2rem] border border-gray-100 shadow-sm">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-sm font-bold text-gray-400 uppercase tracking-widest">{t('faultReports.loadingReports')}</p>
        </div> :
      faults.length === 0 ?
      <div className="bg-white rounded-[2rem] border border-dashed border-gray-200 p-24 text-center">
          <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <FiAlertTriangle className="w-10 h-10 text-gray-300" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{t('faultReports.allClear')}</h3>
          <p className="text-gray-500 mt-2 font-medium">{t('faultReports.noIssues')}</p>
          <button
          onClick={() => setShowCreateModal(true)}
          className="mt-8 px-8 py-3 bg-blue-50 text-blue-600 rounded-xl font-bold hover:bg-blue-100 transition-all">

            {t('faultReports.createFirst')}
          </button>
        </div> :

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {faults.map((fault) =>
        <FaultReportCard key={fault.id} fault={fault} onClick={() => navigate(`/fault-reports/${fault.id}`)} />
        )}
        </div>
      }

      {showCreateModal &&
      <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-in zoom-in duration-300">
            <div className="sticky top-0 bg-white/80 backdrop-blur-md border-b border-gray-100 px-10 py-6 flex items-center justify-between z-10">
              <div>
                <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight">{t('faultReports.newReport')}</h2>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-1">{t('faultReports.submitRequest')}</p>
              </div>
              <button
              onClick={() => setShowCreateModal(false)}
              className="p-3 hover:bg-gray-100 rounded-full transition-colors">

                <LuX className="w-6 h-6 text-gray-400" />
              </button>
            </div>
            <div className="p-10">
              <CreateFaultForm onSubmit={handleCreateFault} onCancel={() => setShowCreateModal(false)} />
            </div>
          </div>
        </div>
      }
    </div>);

}